import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { mainButton } from '@tma.js/sdk-react'
import { useTranslation } from '../../i18n'
import { useTheme } from '../../hooks/useTheme'
import { useAuth } from '../../contexts/AuthContext'
import { userApi, type ApiError } from '../../api'
import { entitlementsOf } from '../../utils/entitlements'
import { useSubscription } from '../../hooks/useSubscription'
import { Button } from '../ui'
import { captureEvent } from '../../posthog'

export default function InviteTrainee() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, translate } = useTranslation()
  const theme = useTheme()
  const { user } = useAuth()
  const entitlements = entitlementsOf(user)
  const canAddTrainee = entitlements.canAddTrainee
  const hasActiveCoach = entitlements.hasActiveCoach
  const { usage } = useSubscription()

  const [username, setUsername] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showInstructions, setShowInstructions] = useState(false)
  const [blockedByLimit, setBlockedByLimit] = useState(false)

  const fromBecomeSpecialist = (location.state as { fromBecomeSpecialist?: boolean })?.fromBecomeSpecialist || false
  const atClientLimit = !hasActiveCoach && (!canAddTrainee || blockedByLimit)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canAddTrainee) {
      if (hasActiveCoach) {
        navigate('/my-coaches')
      } else {
        setBlockedByLimit(true)
      }
      return
    }

    if (!username.trim()) return

    const trimmedUsername = username.trim()
    const trimmedUsernameLower = trimmedUsername.toLowerCase()
    const currentUsername = user?.username?.toLowerCase()
    if (currentUsername && trimmedUsernameLower === currentUsername) {
      setError(t.invite.errors.selfInvite)
      return
    }

    setSaving(true)
    setError(null)

    try {
      console.log('User data:', user)
      const userId = user?.id

      if (!userId) {
        console.error('No user ID found in initData')
        setError('User authentication failed. Please restart the app.')
        setSaving(false)
        return
      }

      console.log('Sending invite:', { userId, username: trimmedUsername })

      await userApi.inviteTrainee({
        userId,
        inviteeUsername: trimmedUsername,
      })
      captureEvent('trainee_invited', { invitee_username: trimmedUsername })

      if (mainButton.mount.isAvailable()) {
        mainButton.mount()
        mainButton.setText(`✅ ${t.invite.button.success}`)
        mainButton.show()
        setTimeout(() => {
          mainButton.hide()
          navigate('/')
        }, 1500)
      } else {
        navigate('/')
      }
    } catch (err) {
      console.error('Error sending invite:', err)

      const apiError = err as ApiError
      const errorMsg = apiError.message?.toLowerCase() || ''
      if (errorMsg.includes('trainees.exceeded') || errorMsg.includes('limit.trainees')) {
        setBlockedByLimit(true)
        return
      }

      let errorMessage = t.invite.errors.unknown

      if (apiError.status === 0) {
        errorMessage = t.common.errors.networkError
      } else if (apiError.status === 404) {
        errorMessage = translate('invite.errors.userNotFound', { username: trimmedUsername })
      } else if (apiError.status === 409) {
        errorMessage = t.invite.errors.alreadyInvited
      } else if (apiError.status === 400) {
        const errorMsg = apiError.message?.toLowerCase() || ''
        if (errorMsg.includes('invite yourself') || errorMsg.includes('запросити самого себе')) {
          errorMessage = t.invite.errors.selfInvite
        } else if (errorMsg.includes('notallowedwhencoached') || errorMsg.includes('addtrainee.notallowed')) {
          errorMessage = translate('subscription.upgradePrompt.leaveCoachToInvite')
        } else {
          errorMessage = apiError.message || t.invite.errors.unknown
        }
      } else if (apiError.status >= 500) {
        errorMessage = t.common.errors.serverError
      } else if (apiError.message) {
        errorMessage = apiError.message
      }

      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const steps = [
    t.invite.info.step1,
    t.invite.info.step2,
    t.invite.info.step3,
    t.invite.info.step4,
    t.invite.info.step5,
  ]

  return (
    <div className="p-5">
      <header className="mb-6">
        <h1 className="text-2xl font-bold mb-1" style={{ color: theme.text.primary }}>
          {t.invite.title}
        </h1>
        <p className="text-sm" style={{ color: theme.text.muted }}>
          {t.invite.subtitle}
        </p>
      </header>

      {hasActiveCoach && (
        <div
          className="mb-5 rounded-xl p-4 flex items-start gap-3"
          style={{ backgroundColor: `${theme.buttonColor}10`, border: `1px solid ${theme.buttonColor}25` }}
        >
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ backgroundColor: `${theme.buttonColor}20` }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.buttonColor }}>
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4"/>
              <path d="M12 16h.01"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold mb-1" style={{ color: theme.text.primary }}>
              {translate('subscription.upgradePrompt.leaveCoachTitle')}
            </p>
            <p className="text-xs leading-relaxed" style={{ color: theme.text.muted }}>
              {translate('subscription.upgradePrompt.leaveCoachToInvite')}
            </p>
          </div>
        </div>
      )}

      {atClientLimit && (
        <div
          className="mb-5 rounded-xl p-4 flex items-start gap-3"
          style={{ backgroundColor: `${theme.buttonColor}10`, border: `1px solid ${theme.buttonColor}25` }}
        >
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ backgroundColor: `${theme.buttonColor}20` }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.buttonColor }}>
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4"/>
              <path d="M12 16h.01"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold mb-1" style={{ color: theme.text.primary }}>
              {t.invite.limitReached.title}
            </p>
            <p className="text-xs leading-relaxed" style={{ color: theme.text.muted }}>
              {t.invite.limitReached.body}
            </p>
            {usage && usage.maxTrainees < 10000 && (
              <p className="text-xs font-semibold mt-2" style={{ color: theme.text.primary }}>
                {usage.traineeCount}/{usage.maxTrainees}
              </p>
            )}
          </div>
        </div>
      )}

      {fromBecomeSpecialist && !hasActiveCoach && !atClientLimit && (
        <div
          className="mb-5 rounded-xl p-4 flex items-start gap-3"
          style={{ backgroundColor: `${theme.buttonColor}10`, border: `1px solid ${theme.buttonColor}25` }}
        >
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ backgroundColor: `${theme.buttonColor}20` }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.buttonColor }}>
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4"/>
              <path d="M12 16h.01"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold mb-1" style={{ color: theme.text.primary }}>
              {t.becomeSpecialist.invite.title}
            </p>
            <p className="text-xs leading-relaxed" style={{ color: theme.text.muted }}>
              {t.becomeSpecialist.invite.description}
            </p>
          </div>
        </div>
      )}

      {!hasActiveCoach && !atClientLimit && (
      <div
        className="rounded-xl mb-6 overflow-hidden"
        style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.cardBorder}` }}
      >
        <button
          type="button"
          onClick={() => setShowInstructions(!showInstructions)}
          className="w-full px-4 py-3.5 flex items-center justify-between active:opacity-75 transition-opacity"
        >
          <div className="flex items-center gap-2.5">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${theme.buttonColor}18` }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: theme.buttonColor }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <span className="text-sm font-semibold" style={{ color: theme.text.primary }}>
              {t.invite.info.title}
            </span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              color: theme.text.muted,
              transform: showInstructions ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
              flexShrink: 0,
            }}
          >
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>

        {showInstructions && (
          <div
            className="px-4 pb-4 pt-1"
            style={{ borderTop: `1px solid ${theme.cardBorder}` }}
          >
            <ol className="space-y-2.5 mt-2">
              {steps.map((step, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span
                    className="text-xs font-bold mt-0.5 w-4 flex-shrink-0 text-right"
                    style={{ color: theme.buttonColor }}
                  >
                    {i + 1}.
                  </span>
                  <span className="text-sm leading-relaxed" style={{ color: theme.text.muted }}>
                    {step.replace(/^\d+\.\s*/, '')}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
      )}

      {atClientLimit && (
        <Button
          type="button"
          variant="primary"
          size="md"
          fullWidth
          onClick={() => navigate('/subscription')}
        >
          {t.subscription.unlockCta}
        </Button>
      )}

      {hasActiveCoach && (
        <Button
          type="button"
          variant="primary"
          size="md"
          fullWidth
          onClick={() => navigate('/my-coaches')}
        >
          {translate('subscription.upgradePrompt.leaveCoachCta')}
        </Button>
      )}

      {!hasActiveCoach && !atClientLimit && (
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {error && (
          <div
            className="rounded-xl p-4 flex items-start gap-3"
            style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }}>
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <p className="text-sm flex-1" style={{ color: theme.text.primary }}>{error}</p>
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-xl leading-none active:opacity-75 flex-shrink-0"
              style={{ color: theme.text.muted }}
              aria-label="Close error"
            >
              ×
            </button>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <label htmlFor="username" className="text-sm font-medium" style={{ color: theme.text.primary }}>
            {t.invite.form.usernameRequired}
          </label>
          <div className="relative">
            <span
              className="absolute left-4 top-1/2 -translate-y-1/2 text-base font-medium pointer-events-none"
              style={{ color: theme.text.muted }}
            >
              @
            </span>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => {
                const newValue = e.target.value.replace('@', '')
                setUsername(newValue)
                setError(null)

                const trimmedValue = newValue.trim()
                if (trimmedValue.length > 0) {
                  const currentUsername = user?.username?.toLowerCase()
                  if (currentUsername && trimmedValue.toLowerCase() === currentUsername) {
                    setError(t.invite.errors.selfInvite)
                  }
                }
              }}
              placeholder={t.invite.form.usernamePlaceholder}
              required
              minLength={3}
              maxLength={32}
              pattern="[a-zA-Z0-9_]+"
              disabled={saving}
              className="w-full rounded-xl pl-9 pr-4 py-3 text-base outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: theme.inputBg,
                color: theme.text.primary,
                border: `1px solid ${theme.inputBorder}`,
              }}
            />
          </div>
          <p className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
            {t.invite.form.usernameHelp}
          </p>
        </div>

        <Button
          type="submit"
          disabled={!username.trim() || saving}
          variant="primary"
          size="md"
          fullWidth
        >
          {saving ? t.invite.button.submitting : t.invite.button.submit}
        </Button>

        {!fromBecomeSpecialist && (
          <button
            type="button"
            onClick={() => navigate('/create-virtual-trainee')}
            className="text-sm text-center active:opacity-75 transition-opacity underline"
            style={{ color: theme.text.muted }}
          >
            {t.invite.form.noTelegram}
          </button>
        )}
      </form>
      )}
    </div>
  )
}
